import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Button,
  Table,
  Container,
  Row,
  Col,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import MemberForm from "./MemberForm"; // 🔁 Replace with your actual form component
import PaginationComponent from "./PaginationComponent";
import { useNavigate } from "react-router-dom";
import useApiError from "../../Routes/useApiError";

const PAGE_SIZE = 50;

const AdminMembersPage = () => {
  const handleApiError = useApiError();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [searchName, setSearchName] = useState("");
  const [searchMobile, setSearchMobile] = useState("");
  const [searchPlan, setSearchPlan] = useState("");

  const baseUrl = `${process.env.REACT_APP_COLLEGE_ERP_API}library_member`;
  const userData = JSON.parse(sessionStorage.getItem("userData")) || {};

  const fetchMembers = async (pageNumber = 1) => {
    try {
      const res = await axios.get(baseUrl, {
        params: {
          branch: userData.branch,
          page: pageNumber,
          limit: PAGE_SIZE,
          ...(searchName && { name: searchName }),
          ...(searchMobile && { mobile: searchMobile }),
          ...(searchPlan && { plan: searchPlan }),
        },
        headers: {
          Authorization: `Bearer ${userData.token}`,
        },
      });

      const { data, total } = res.data;
      setMembers(data || []);
      setTotalPages(Math.ceil(total / PAGE_SIZE));
    } catch (err) {
      console.error("Failed to fetch members:", err);
      if (handleApiError(err.response?.data)) return;
      setMembers([]);
      setTotalPages(1);
    }
  };

  useEffect(() => {
    fetchMembers(page);
  }, [page]);

  const handleSubmit = async (formData) => {
    try {
      const method = editData ? "PUT" : "POST";
      const url = editData ? `${baseUrl}` : baseUrl;

      await axios({
        method,
        url,
        params: { branch: userData.branch },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userData.token}`,
        },
        data: formData,
      });

      setModalOpen(false);
      setEditData(null);
      fetchMembers(page);
    } catch (err) {
      console.error("Failed to save member:", err);
    }
  };

  const handleEdit = (member) => {
    setEditData(member);
    setModalOpen(true);
  };

  const handleDetete = async (member) => {
    if (userData.rights !== "Admin" || userData.userId !== "soumya") {
      alert("You do not have permission to delete members.");
      return;
    }

    const enteredId = window.prompt(
      `Please type the Member ID (${member.Member_ID}) to confirm deletion:`
    );

    if (!enteredId) {
      alert("Deletion cancelled. Member ID is required.");
      return;
    }

    if (enteredId === member.Member_ID.toString()) {
      try {
        await axios.delete(`${baseUrl}`, {
          params: {
            branch: userData.branch,
            id: member._id,
            userId: userData.userId,
          },
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        });
        fetchMembers(page);
      } catch (err) {
        console.error("Failed to delete member:", err);
      }
    }
  };

  return (
    <Container fluid className="mt-4" style={{ paddingBottom: "100px" }}> {/* Added safety buffer at the very bottom of page */}
      <Row>
        <Col md={4}>
          <FormGroup>
            <Label for="searchName">Name</Label>
            <Input
              id="searchName"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              placeholder="Search by name"
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label for="searchMobile">Mobile Number</Label>
            <Input
              id="searchMobile"
              value={searchMobile}
              onChange={(e) => setSearchMobile(e.target.value)}
              placeholder="Search by mobile"
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label for="searchPlan">Membership Plan</Label>
            <Input
              id="searchPlan"
              value={searchPlan}
              onChange={(e) => setSearchPlan(e.target.value)}
              placeholder="Search by plan"
            />
          </FormGroup>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Button color="primary" onClick={() => fetchMembers(1)}>
            Search
          </Button>
        </Col>
        <Col md={6} className="text-end">
          <Button color="success" onClick={() => setModalOpen(true)}>
            + Add Member
          </Button>
        </Col>
      </Row>

      <Table striped responsive bordered style={{ overflow: "visible" }}>
        <thead>
          <tr>
            <th>#</th>
            <th>Member ID</th>
            <th>Name</th>
            <th>Mobile</th>
            <th>Plan</th>
            <th>Activation</th>
            <th>Last Renewal</th>
            <th>Expiry</th>
            <th>Delivery</th>
            <th>Deposit</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {members?.length ? (
            members.map((member, idx) => (
              <tr key={member._id}>
                <td>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                <td>{member.Member_ID}</td>
                <td>{member.Member_Name}</td>
                <td>{member.Mobile_Number}</td>
                <td>{member.Membership_Plan}</td>
                <td>{member.Membership_Activation_Date}</td>
                <td>{member.Membership_Last_Renewal_Date}</td>
                <td>{member.Membership_Expiry_Date}</td>
                <td>{member.Door_Delivery}</td>
                <td>{member.Deposit_Paid}</td>
                <td>
                  <UncontrolledDropdown>
                    <DropdownToggle
                      caret
                      color="secondary"
                      size="sm"
                      className="bg-primary text-white fw-bold px-3 py-1 rounded"
                      style={{
                        border: "none",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      }}
                    >
                      Actions
                    </DropdownToggle>

                    {/* ⚡ KEY CHANGES: added container="body" and modifiers for smart placement */}
                    <DropdownMenu
                      container="body"
                      modifiers={[
                        {
                          name: "preventOverflow",
                          options: {
                            boundary: "viewport",
                          },
                        },
                      ]}
                      style={{
                        backgroundColor: "#222",
                        minWidth: "160px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                        margin: 0,
                        padding: 0,
                        zIndex: 1050, // Ensures it sits above everything
                      }}
                    >
                      <DropdownItem
                        onClick={() => handleEdit(member)}
                        style={{
                          backgroundColor: "#ffc107",
                          color: "#000",
                          margin: 0,
                        }}
                      >
                        Edit
                      </DropdownItem>

                      <DropdownItem
                        onClick={() => {
                          navigate("/admin/payments", { state: { member } });
                        }}
                        style={{
                          backgroundColor: "#28a745",
                          color: "#fff",
                          margin: 0,
                        }}
                      >
                        Payment
                      </DropdownItem>
                      <DropdownItem
                        onClick={() => handleDetete(member)}
                        style={{
                          backgroundColor: "#d31f13ff",
                          color: "#fff",
                          margin: 0,
                        }}
                      >
                        Delete
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="11" className="text-center"> {/* Fixed from colSpan 10 to 11 */}
                No members found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <PaginationComponent
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      <Modal
        isOpen={modalOpen}
        toggle={() => {
          setModalOpen(false);
          setEditData(null);
        }}
        style={{ maxWidth: "900px", width: "100%" }}
      >
        <ModalHeader
          toggle={() => {
            setModalOpen(false);
            setEditData(null);
          }}
        >
          {editData ? "Edit Member" : "Add Member"}
        </ModalHeader>
        <ModalBody>
          <MemberForm onSubmit={handleSubmit} editData={editData} />
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default AdminMembersPage;